import React from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { InfluencerSubmission, SubmissionMediaItem } from "@/lib/interfaces";
import { Save, Trash2, Plus, Image, Video, X, Calendar } from "lucide-react";

interface SubmissionEditDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  submission: InfluencerSubmission | null;
  onSave: (id: number, data: {
    caption?: string;
    tags?: string[];
    media?: SubmissionMediaItem[];
    platform?: string;
    expected_publication_date?: string | null;
    objective?: string | null;
  }) => void;
  isSaving?: boolean;
}

export default function SubmissionEditDialog({
  open,
  onOpenChange,
  submission,
  onSave,
  isSaving,
}: SubmissionEditDialogProps) {
  const [caption, setCaption] = React.useState("");
  const [tags, setTags] = React.useState<string[]>([]);
  const [newTag, setNewTag] = React.useState("");
  const [platform, setPlatform] = React.useState("");
  const [objective, setObjective] = React.useState("");
  const [expectedDate, setExpectedDate] = React.useState("");
  const [media, setMedia] = React.useState<SubmissionMediaItem[]>([]);
  const [newMediaUrl, setNewMediaUrl] = React.useState("");
  const [newMediaType, setNewMediaType] = React.useState<"image" | "video" | "reel">("image");

  React.useEffect(() => {
    if (submission) {
      setCaption(submission.caption || "");
      setTags(submission.tags || []);
      setPlatform(submission.platform || "");
      setObjective(submission.objective || "");
      setExpectedDate(submission.expected_publication_date || "");
      setMedia(submission.media || []);
    }
  }, [submission]);

  React.useEffect(() => {
    if (!open) {
      setNewMediaUrl("");
      setNewMediaType("image");
      setNewTag("");
    }
  }, [open]);

  if (!submission) return null;

  const handleAddMedia = () => {
    if (!newMediaUrl.trim()) return;
    setMedia([...media, { url: newMediaUrl.trim(), type: newMediaType }]);
    setNewMediaUrl("");
  };

  const handleRemoveMedia = (index: number) => {
    setMedia(media.filter((_, i) => i !== index));
  };

  const handleAddTag = () => {
    const tag = newTag.trim().replace(/^#/, "");
    if (tag && !tags.includes(tag)) {
      setTags([...tags, tag]);
    }
    setNewTag("");
  };

  const handleRemoveTag = (tagToRemove: string) => {
    setTags(tags.filter((t) => t !== tagToRemove));
  };

  const handleSave = () => {
    onSave(submission.id, {
      caption: caption.trim(),
      tags,
      media,
      platform: platform.trim() || undefined,
      expected_publication_date: expectedDate || null,
      objective: objective.trim() || null,
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-full max-w-full !max-w-[98vw] h-[95vh] flex flex-col p-8">
        <DialogHeader>
          <DialogTitle className="text-2xl">Edit Submission</DialogTitle>
          <DialogDescription>
            Modify the submission details before approving. Changes will be saved immediately.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-6 flex-1 overflow-y-auto">
          {/* Caption */}
          <div className="space-y-2">
            <Label htmlFor="caption" className="text-sm font-medium">
              Caption <span className="text-red-500">*</span>
            </Label>
            <Textarea
              id="caption"
              value={caption}
              onChange={(e) => setCaption(e.target.value)}
              placeholder="Post caption..."
              rows={4}
              className="text-base"
            />
          </div>

          {/* Platform & Expected Date */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label htmlFor="platform" className="text-sm font-medium">
                Platform
              </Label>
              <Input
                id="platform"
                value={platform}
                onChange={(e) => setPlatform(e.target.value)}
                placeholder="Instagram, TikTok, YouTube..."
                className="text-base"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="expected-date" className="text-sm font-medium flex items-center gap-1">
                <Calendar className="h-4 w-4" /> Expected Publication Date
              </Label>
              <Input
                id="expected-date"
                type="date"
                value={expectedDate ? expectedDate.split("T")[0] : ""}
                onChange={(e) => setExpectedDate(e.target.value || "")}
                className="text-base"
              />
            </div>
          </div>

          {/* Objective */}
          <div className="space-y-2">
            <Label htmlFor="objective" className="text-sm font-medium">
              Objective
            </Label>
            <Textarea
              id="objective"
              value={objective}
              onChange={(e) => setObjective(e.target.value)}
              placeholder="Campaign objective..."
              rows={2}
              className="text-base"
            />
          </div>

          {/* Tags */}
          <div className="space-y-2">
            <Label className="text-sm font-medium">Tags</Label>
            <div className="flex flex-wrap gap-2 mb-2">
              {tags.map((tag, index) => (
                <span
                  key={index}
                  className="px-3 py-1 bg-gray-100 rounded-full text-sm font-medium flex items-center gap-1"
                >
                  #{tag}
                  <button
                    type="button"
                    onClick={() => handleRemoveTag(tag)}
                    className="text-gray-500 hover:text-red-500"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </span>
              ))}
            </div>
            <div className="flex gap-2">
              <Input
                value={newTag}
                onChange={(e) => setNewTag(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    handleAddTag();
                  }
                }}
                placeholder="Add a tag..."
                className="flex-1"
              />
              <Button type="button" onClick={handleAddTag} disabled={!newTag.trim()}>
                <Plus className="h-4 w-4 mr-1" /> Add
              </Button>
            </div>
          </div>

          {/* Current Media */}
          <div className="space-y-4">
            <Label className="text-sm font-medium">Media ({media.length} items)</Label>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {media.map((item, index) => (
                <div
                  key={index}
                  className="relative aspect-square rounded-xl overflow-hidden border-2 bg-muted group"
                >
                  {item.type === "video" || item.type === "reel" ? (
                    <video
                      src={item.url}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <img
                      src={item.url}
                      alt={`Media ${index + 1}`}
                      className="w-full h-full object-cover"
                    />
                  )}
                  <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => handleRemoveMedia(index)}
                    >
                      <Trash2 className="h-4 w-4 mr-1" />
                      Remove
                    </Button>
                  </div>
                  <span className="absolute top-2 right-2 bg-black/70 text-white text-xs px-2 py-1 rounded-full capitalize">
                    {item.type}
                  </span>
                </div>
              ))}
            </div>

            {/* Add New Media */}
            <div className="bg-gray-50 border-2 border-dashed rounded-xl p-6 space-y-4">
              <Label className="text-sm font-medium">Add New Media</Label>
              <div className="flex gap-4 items-end">
                <div className="flex-1 space-y-2">
                  <Label htmlFor="media-url" className="text-xs text-muted-foreground">
                    Media URL
                  </Label>
                  <Input
                    id="media-url"
                    value={newMediaUrl}
                    onChange={(e) => setNewMediaUrl(e.target.value)}
                    placeholder="https://example.com/image.jpg"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs text-muted-foreground">Type</Label>
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      variant={newMediaType === "image" ? "default" : "outline"}
                      size="sm"
                      onClick={() => setNewMediaType("image")}
                    >
                      <Image className="h-4 w-4 mr-1" />
                      Image
                    </Button>
                    <Button
                      type="button"
                      variant={newMediaType === "video" ? "default" : "outline"}
                      size="sm"
                      onClick={() => setNewMediaType("video")}
                    >
                      <Video className="h-4 w-4 mr-1" />
                      Video
                    </Button>
                    <Button
                      type="button"
                      variant={newMediaType === "reel" ? "default" : "outline"}
                      size="sm"
                      onClick={() => setNewMediaType("reel")}
                    >
                      <Video className="h-4 w-4 mr-1" />
                      Reel
                    </Button>
                  </div>
                </div>
                <Button
                  type="button"
                  onClick={handleAddMedia}
                  disabled={!newMediaUrl.trim()}
                >
                  <Plus className="h-4 w-4 mr-1" />
                  Add
                </Button>
              </div>
            </div>
          </div>

          {/* Submission Info (Read-only) */}
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
            <Label className="text-sm font-medium text-blue-700">Submission Info</Label>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-3 text-sm">
              <div>
                <span className="text-blue-600">Influencer:</span>
                <p className="font-medium">
                  {submission.influencer?.username || submission.influencer?.email || `#${submission.influencer_id}`}
                </p>
              </div>
              <div>
                <span className="text-blue-600">Date:</span>
                <p className="font-medium">{new Date(submission.created_at).toLocaleString()}</p>
              </div>
              <div>
                <span className="text-blue-600">Status:</span>
                <p className="font-medium capitalize">{submission.status}</p>
              </div>
              <div>
                <span className="text-blue-600">ID:</span>
                <p className="font-medium">#{submission.id}</p>
              </div>
            </div>
          </div>
        </div>

        <DialogFooter className="gap-3 pt-4 border-t">
          <Button
            variant="outline"
            size="lg"
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </Button>
          <Button
            size="lg"
            onClick={handleSave}
            disabled={!caption.trim() || isSaving}
          >
            <Save className="mr-2 h-5 w-5" />
            {isSaving ? "Saving..." : "Save Changes"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
